import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { CartItem } from '@/types/cart.types';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { CartClearReason } from '@/types/cart-clear-confirmation.types';
import {
  AddMoreProceduresDestination,
  CartChangePatientScope,
  CartClearMotive,
  CartOrigin,
  CartRemovalScope,
} from '@/types/enums/analytics/cart/cart-analytics.enum';
import { PatientOption } from '@/types/patient-selection.types';
import { getCartItemScheduleType } from '@/utils/cart/get-cart-item-schedule-type.util';
import {
  getCartCouponDiscountTotal,
  getCartTotal,
  getCouponAppliedItemCount,
} from '@/utils/cart/get-cart-total.util';

function sharedValue<T>(values: T[]): T | undefined {
  return new Set(values).size === 1 ? values[0] : undefined;
}

export function trackCartOpened(origin: CartOrigin, items: CartItem[]): void {
  getAnalyticsPlugin().track(AnalyticsEnum.OPEN_CART, {
    origem: origin,
    qtd_itens: items.length,
    valor_total: getCartTotal(items),
  });
}

export function trackAddMoreProcedures(
  origin: CartOrigin,
  items: CartItem[],
  destination: AddMoreProceduresDestination,
): void {
  getAnalyticsPlugin().track(AnalyticsEnum.ADD_MORE_PROCEDURES, {
    origem: origin,
    qtd_itens: items.length,
    destino: destination,
  });
}

export function trackCartItemAdded(item: CartItem): void {
  getAnalyticsPlugin().track(AnalyticsEnum.ADD_ITEM, {
    tipo_agenda: getCartItemScheduleType(item),
    partner_id: item.partner.id,
    valor: item.discountedPrice ?? item.price,
  });
}

export function trackCartItemsRemoved(
  scope: CartRemovalScope,
  removedItems: CartItem[],
  remainingItems: CartItem[],
): void {
  getAnalyticsPlugin().track(AnalyticsEnum.REMOVE_ITEMS, {
    escopo: scope,
    qtd_removidos: removedItems.length,
    qtd_itens: remainingItems.length,
    valor_total: getCartTotal(remainingItems),
    tipo_agenda: sharedValue(removedItems.map(getCartItemScheduleType)),
    partner_id: sharedValue(removedItems.map((item) => item.partner.id)),
  });
}

export function trackCouponApplied(code: string, items: CartItem[]): void {
  getAnalyticsPlugin().track(AnalyticsEnum.APPLY_COUPON, {
    cupom_code: code,
    desconto_total: getCartCouponDiscountTotal(items),
    itens_elegiveis: getCouponAppliedItemCount(items),
    itens_total: items.length,
  });
}

export function trackCouponRemoved(code: string, wasForced: boolean): void {
  getAnalyticsPlugin().track(AnalyticsEnum.REMOVE_COUPON, {
    cupom_code: code,
    forcado: wasForced,
  });
}

export function trackCouponError(code: string, reason: string): void {
  getAnalyticsPlugin().track(AnalyticsEnum.COUPON_ERROR, {
    cupom_code: code,
    motivo: reason,
  });
}

function changePatientPayload(scope: CartChangePatientScope, items: CartItem[]) {
  const [firstItem] = items;

  return {
    escopo: scope,
    qtd_itens: items.length,
    procedimento: firstItem?.procedure.name,
    paciente_atual: firstItem?.patient.id,
  };
}

export function trackChangePatientOpened(scope: CartChangePatientScope, items: CartItem[]): void {
  getAnalyticsPlugin().track(AnalyticsEnum.OPEN_CHANGE_PATIENT, changePatientPayload(scope, items));
}

export function trackPatientChanged(
  scope: CartChangePatientScope,
  items: CartItem[],
  newPatient: Pick<PatientOption, 'id' | 'type'>,
): void {
  getAnalyticsPlugin().track(AnalyticsEnum.CHANGE_PATIENT, {
    ...changePatientPayload(scope, items),
    tipo_agenda: sharedValue(items.map(getCartItemScheduleType)),
    paciente_novo: newPatient.id,
    paciente_tipo: newPatient.type,
  });
}

const motiveByReason: Record<CartClearReason, CartClearMotive> = {
  [CartClearReason.ADDING_STANDALONE]: CartClearMotive.ADDING_STANDALONE,
  [CartClearReason.CART_HAS_STANDALONE]: CartClearMotive.CART_HAS_STANDALONE,
};

export function trackCartClearConfirmationShown(reason: CartClearReason, items: CartItem[]): void {
  getAnalyticsPlugin().track(AnalyticsEnum.VIEW_CART_CLEAR_CONFIRMATION, {
    motivo: motiveByReason[reason],
    qtd_itens: items.length,
  });
}

export function trackCartClearAnswer(reason: CartClearReason, confirmed: boolean, items: CartItem[]): void {
  getAnalyticsPlugin().track(
    confirmed ? AnalyticsEnum.CONFIRM_CART_CLEAR : AnalyticsEnum.CANCEL_CART_CLEAR,
    {
      motivo: motiveByReason[reason],
      qtd_itens: items.length,
    },
  );
}
